import React from "react";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { useEffect, useState, useMemo } from "react";
import AgGridTable from "../../../components/table/AgGridTable";
import {
  Box,
  Card,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RescanConfirmDialog from "./RescanConfirmDialog";
import axiosInstance from "../../../axios/axios";
import { END_POINTS } from "../../../shared";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import IOSSwitch from "../../../components/IOSSwitch";
import { getStatus } from "../../../axios/constants";
import calenderIcon from "../../.././assets/baseVersion/calender.png";
import { useDispatch, useSelector } from "react-redux";
import { myScanActions } from "../../../store/onPremMyscanSlice";
import RenameModel from "./RenameModel";
import ChangeScheduleModal from "./ChangeScheduleModal";
import AddIcon from "@mui/icons-material/Add";
import { setScheduleList } from "./Schedule_Slice";
import { setEdit, setScanDetails } from "./MyScanTable_Details_slice";
import { RootState } from "../../../store/store";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";
import green_shield from "../../../assets/green-shield.svg";
import orange_shield from "../../../assets/orange-shield.svg";
import red_shield from "../../../assets/red-shield.svg";
import misconfig from "../../../assets/misconfig.svg";
import useRolePermissions from "../../../shared/hooks/useRolePermissions";
import { ROLES_CONTENT_PROPS } from "../../../shared/constants/constant-data";
import { useStorageManager } from "../../../shared/hooks";
import buleCalenderIcon from '../../../assets/bule_schedule_calender.svg'
import { resetUserTypeFlags, setIsBothCloudAndPremAssetUser, setIsNewUserTrue, setIsOnlyCloudUser, setIsOnlyPremAssetUser } from "../../../modules/authentication/login/Login_Details_slice";

// 🚀 Global variables to store filter data for AG Grid filters
let globalStatuses: string[] = [];
let globalUsers: string[] = [];
let globalScheduleStatuses: string[] = [];

// 🚀 Global variables for Schedule Table filters
let globalScheduleFrequencies: string[] = [];
let globalScheduleCreatedBy: string[] = [];
let globalScheduleScheduledStatuses: string[] = [];
let globalScheduleScanNames: string[] = [];

// 🚀 Custom Status Filter Component for AG Grid
const StatusFilter = React.forwardRef<any, any>((params, ref) => {
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [availableStatuses, setAvailableStatuses] = useState<string[]>(globalStatuses);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');

  // Filter statuses based on search text
  const filteredStatuses = React.useMemo(() => {
    if (!searchText.trim()) return availableStatuses;
    return availableStatuses.filter(status =>
      status.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [availableStatuses, searchText]);

  // Update available statuses when global variable changes
  React.useEffect(() => {
    const checkForUpdates = () => {
      if (globalStatuses.length > 0 && availableStatuses.length === 0) {
        setAvailableStatuses([...globalStatuses]);
      }
    };

    const interval = setInterval(checkForUpdates, 500);
    checkForUpdates();

    return () => clearInterval(interval);
  }, [availableStatuses.length]);

  React.useImperativeHandle(ref, () => ({
    isFilterActive: () => {
      const isActive = selectedStatuses.length > 0 && !isAllSelected;
      return isActive;
    },

    doesFilterPass: (params: any) => {
      if (selectedStatuses.length === 0 || isAllSelected) return true;
      return selectedStatuses.includes(params.data.status);
    },

    getModel: () => {
      return selectedStatuses.length > 0 && !isAllSelected ? { selectedTypes: selectedStatuses } : null;
    },

    setModel: (model: any) => {
      if (model && model.selectedTypes) {
        setSelectedStatuses(model.selectedTypes);
        setIsAllSelected(false);
      } else {
        setSelectedStatuses([]);
        setIsAllSelected(true);
      }
    },

    afterGuiAttached: () => {
      setAvailableStatuses([...globalStatuses]);
    },

    getGui: () => {
      return null;
    }
  }));

  const handleStatusToggle = (status: string) => {
    if (status === 'All') {
      setIsAllSelected(true);
      setSelectedStatuses([]);
    } else {
      setIsAllSelected(false);
      const newSelected = selectedStatuses.includes(status)
        ? selectedStatuses.filter(type => type !== status)
        : [...selectedStatuses, status];

      setSelectedStatuses(newSelected);
    }

    setTimeout(() => {
      if (params.api) {
        params.api.onFilterChanged();
      }
    }, 0);
  };

  const handleClear = () => {
    setSelectedStatuses([]);
    setIsAllSelected(true);
    setSearchText('');
    (window as any).statusFilter = [];
    window.dispatchEvent(new CustomEvent('statusFilterChanged', {
      detail: { selectedTypes: [] }
    }));
  };

  const isDarkTheme = params.isDarkMode ||
    params.api?.gridOptionsWrapper?.gridOptions?.theme === 'dark' ||
    document.documentElement.classList.contains('dark') ||
    document.body.classList.contains('dark-theme');

  return (
    <div style={{
      padding: '12px',
      minWidth: '240px',
      maxWidth: '260px',
      backgroundColor: isDarkTheme ? '#1e1e1e' : '#ffffff',
      border: `1px solid ${isDarkTheme ? '#333333' : '#d0d7de'}`,
      borderRadius: '6px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: '13px'
    }}>
      <div style={{
        marginBottom: '12px',
        fontWeight: '500',
        color: isDarkTheme ? '#f7fafc' : '#2d3748',
        fontSize: '13px'
      }}>
        Filter Status
      </div>

      {/* Search Input */}
      <div style={{ marginBottom: '12px' }}>
        <input
          type="text"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{
            width: '100%',
            padding: '6px 8px',
            border: `1px solid ${isDarkTheme ? '#444444' : '#d0d7de'}`,
            borderRadius: '4px',
            backgroundColor: isDarkTheme ? '#2a2a2a' : '#ffffff',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px',
            outline: 'none',
            boxSizing: 'border-box'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#0969da';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = isDarkTheme ? '#444444' : '#d0d7de';
          }}
        />
      </div>

      <div style={{
        maxHeight: '200px',
        overflowY: 'auto',
        marginBottom: '12px'
      }}>
        {/* All option */}
        <div
          key="All"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '6px 8px',
            cursor: 'pointer',
            backgroundColor: isAllSelected ? (isDarkTheme ? '#0969da20' : '#dbeafe') : 'transparent',
            borderRadius: '4px',
            marginBottom: '2px',
            fontWeight: isAllSelected ? '500' : '400',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px'
          }}
          onClick={() => handleStatusToggle('All')}
        >
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={() => handleStatusToggle('All')}
            style={{ marginRight: '8px' }}
          />
          <span>All</span>
        </div>

        {filteredStatuses.length > 0 ? (
          filteredStatuses.map((status: string) => {
            const isSelected = selectedStatuses.includes(status);
            return (
              <div
                key={status}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '6px 8px',
                  cursor: 'pointer',
                  backgroundColor: isSelected ? (isDarkTheme ? '#0969da20' : '#dbeafe') : 'transparent',
                  borderRadius: '4px',
                  marginBottom: '2px',
                  color: isDarkTheme ? '#f7fafc' : '#2d3748',
                  fontSize: '13px'
                }}
                onClick={() => handleStatusToggle(status)}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleStatusToggle(status)}
                  style={{
                    marginRight: '8px'
                  }}
                />
                <span>{status}</span>
              </div>
            );
          })
        ) : availableStatuses.length === 0 ? (
          <div style={{ color: isDarkTheme ? '#a0aec0' : '#718096', fontStyle: 'italic' }}>
            Loading statuses...
          </div>
        ) : (
          <div style={{ color: isDarkTheme ? '#a0aec0' : '#718096', fontStyle: 'italic' }}>
            No status found matching
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={handleClear}
          style={{
            padding: '6px 12px',
            backgroundColor: 'transparent',
            border: `1px solid ${isDarkTheme ? '#444444' : '#d0d7de'}`,
            borderRadius: '4px',
            cursor: 'pointer',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px'
          }}
        >
          Clear
        </button>
        <button
          onClick={() => {
            (window as any).statusFilter = isAllSelected ? [] : selectedStatuses;
            window.dispatchEvent(new CustomEvent('statusFilterChanged', {
              detail: { selectedTypes: isAllSelected ? [] : selectedStatuses }
            }));
            // close the AG Grid filter popup
            params.api?.hidePopupMenu?.();
          }}
          style={{
            padding: '6px 12px',
            backgroundColor: '#0969da',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px'
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );
});

// 🚀 Custom User Filter Component for AG Grid
const UserFilter = React.forwardRef<any, any>((params, ref) => {
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [availableUsers, setAvailableUsers] = useState<string[]>(params.users || globalUsers);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');

  // Filter users based on search text
  const filteredUsers = React.useMemo(() => {
    if (!searchText.trim()) return availableUsers;
    return availableUsers.filter(user =>
      user.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [availableUsers, searchText]);

  // Update available users when params or global variable changes
  React.useEffect(() => {
    const usersToUse = params.users || globalUsers;
    if (usersToUse.length > 0) {
      setAvailableUsers([...usersToUse]);
    }
  }, [params.users]);

  React.useImperativeHandle(ref, () => ({
    isFilterActive: () => {
      const isActive = selectedUsers.length > 0 && !isAllSelected;
      return isActive;
    },

    doesFilterPass: (params: any) => {
      if (selectedUsers.length === 0 || isAllSelected) return true;
      return selectedUsers.includes(params.data.user);
    },

    getModel: () => {
      return selectedUsers.length > 0 && !isAllSelected ? { selectedTypes: selectedUsers } : null;
    },

    setModel: (model: any) => {
      if (model && model.selectedTypes) {
        setSelectedUsers(model.selectedTypes);
        setIsAllSelected(false);
      } else {
        setSelectedUsers([]);
        setIsAllSelected(true);
      }
    },

    afterGuiAttached: () => {
      setAvailableUsers([...globalUsers]);
    },

    getGui: () => {
      return null;
    }
  }));

  const handleUserToggle = (user: string) => {
    if (user === 'All') {
      setIsAllSelected(true);
      setSelectedUsers([]);
    } else {
      setIsAllSelected(false);
      const newSelected = selectedUsers.includes(user)
        ? selectedUsers.filter(type => type !== user)
        : [...selectedUsers, user];

      setSelectedUsers(newSelected);
    }

    setTimeout(() => {
      if (params.api) {
        params.api.onFilterChanged();
      }
    }, 0);
  };

  const handleClear = () => {
    setSelectedUsers([]);
    setIsAllSelected(true);
    setSearchText('');
    (window as any).userFilter = [];
    window.dispatchEvent(new CustomEvent('userFilterChanged', {
      detail: { selectedTypes: [] }
    }));
  };

  const isDarkTheme = params.isDarkMode ||
    params.api?.gridOptionsWrapper?.gridOptions?.theme === 'dark' ||
    document.documentElement.classList.contains('dark') ||
    document.body.classList.contains('dark-theme');

  return (
    <div style={{
      padding: '12px',
      minWidth: '240px',
      maxWidth: '260px',
      backgroundColor: isDarkTheme ? '#1e1e1e' : '#ffffff',
      border: `1px solid ${isDarkTheme ? '#333333' : '#d0d7de'}`,
      borderRadius: '6px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: '13px'
    }}>
      <div style={{
        marginBottom: '12px',
        fontWeight: '500',
        color: isDarkTheme ? '#f7fafc' : '#2d3748',
        fontSize: '13px'
      }}>
        Filter Users
      </div>

      {/* Search Input */}
      <div style={{ marginBottom: '12px' }}>
        <input
          type="text"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{
            width: '100%',
            padding: '6px 8px',
            border: `1px solid ${isDarkTheme ? '#444444' : '#d0d7de'}`,
            borderRadius: '4px',
            backgroundColor: isDarkTheme ? '#2a2a2a' : '#ffffff',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px',
            outline: 'none',
            boxSizing: 'border-box'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#0969da';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = isDarkTheme ? '#444444' : '#d0d7de';
          }}
        />
      </div>

      <div style={{
        maxHeight: '200px',
        overflowY: 'auto',
        marginBottom: '12px'
      }}>
        {/* All option */}
        <div
          key="All"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '6px 8px',
            cursor: 'pointer',
            backgroundColor: isAllSelected ? (isDarkTheme ? '#0969da20' : '#dbeafe') : 'transparent',
            borderRadius: '4px',
            marginBottom: '2px',
            fontWeight: isAllSelected ? '500' : '400',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px'
          }}
          onClick={() => handleUserToggle('All')}
        >
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={() => handleUserToggle('All')}
            style={{ marginRight: '8px' }}
          />
          <span>All</span>
        </div>

        {filteredUsers.length > 0 ? (
          filteredUsers.map((user: string) => {
            const isSelected = selectedUsers.includes(user);
            return (
              <div
                key={user}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '6px 8px',
                  cursor: 'pointer',
                  backgroundColor: isSelected ? (isDarkTheme ? '#0969da20' : '#dbeafe') : 'transparent',
                  borderRadius: '4px',
                  marginBottom: '2px',
                  color: isDarkTheme ? '#f7fafc' : '#2d3748',
                  fontSize: '13px'
                }}
                onClick={() => handleUserToggle(user)}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleUserToggle(user)}
                  style={{
                    marginRight: '8px'
                  }}
                />
                <span>{user}</span>
              </div>
            );
          })
        ) : availableUsers.length === 0 ? (
          <div style={{ color: isDarkTheme ? '#a0aec0' : '#718096', fontStyle: 'italic' }}>
            Loading users...
          </div>
        ) : (
          <div style={{ color: isDarkTheme ? '#a0aec0' : '#718096', fontStyle: 'italic' }}>
            No users found matching
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={handleClear}
          style={{
            padding: '6px 12px',
            backgroundColor: 'transparent',
            border: `1px solid ${isDarkTheme ? '#444444' : '#d0d7de'}`,
            borderRadius: '4px',
            cursor: 'pointer',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px'
          }}
        >
          Clear
        </button>
        <button
          onClick={() => {
            (window as any).userFilter = isAllSelected ? [] : selectedUsers;
            window.dispatchEvent(new CustomEvent('userFilterChanged', {
              detail: { selectedTypes: isAllSelected ? [] : selectedUsers }
            }));
            params.api?.hidePopupMenu?.();
          }}
          style={{
            padding: '6px 12px',
            backgroundColor: '#0969da',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px'
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );
});

// 🚀 Custom Schedule Status Filter Component for AG Grid
const ScheduleStatusFilter = React.forwardRef<any, any>((params, ref) => {
  const [selectedScheduleStatuses, setSelectedScheduleStatuses] = useState<string[]>([]);
  const [availableScheduleStatuses, setAvailableScheduleStatuses] = useState<string[]>(params.scheduleStatuses || globalScheduleStatuses);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');

  // Filter schedule statuses based on search text
  const filteredScheduleStatuses = React.useMemo(() => {
    if (!searchText.trim()) return availableScheduleStatuses;
    return availableScheduleStatuses.filter(scheduleStatus =>
      scheduleStatus.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [availableScheduleStatuses, searchText]);

  // Update available schedule statuses when params or global variable changes
  React.useEffect(() => {
    const scheduleStatusesToUse = params.scheduleStatuses || globalScheduleStatuses;
    if (scheduleStatusesToUse.length > 0) {
      setAvailableScheduleStatuses([...scheduleStatusesToUse]);
    }
  }, [params.scheduleStatuses]);

  React.useImperativeHandle(ref, () => ({
    isFilterActive: () => {
      const isActive = selectedScheduleStatuses.length > 0 && !isAllSelected;
      return isActive;
    },

    doesFilterPass: (params: any) => {
      if (selectedScheduleStatuses.length === 0 || isAllSelected) return true;
      return selectedScheduleStatuses.includes(params.data.schedule_status);
    },

    getModel: () => {
      return selectedScheduleStatuses.length > 0 && !isAllSelected ? { selectedTypes: selectedScheduleStatuses } : null;
    },

    setModel: (model: any) => {
      if (model && model.selectedTypes) {
        setSelectedScheduleStatuses(model.selectedTypes);
        setIsAllSelected(false);
      } else {
        setSelectedScheduleStatuses([]);
        setIsAllSelected(true);
      }
    },

    afterGuiAttached: () => {
      setAvailableScheduleStatuses([...globalScheduleStatuses]);
    },

    getGui: () => {
      return null;
    }
  }));

  const handleScheduleStatusToggle = (scheduleStatus: string) => {
    if (scheduleStatus === 'All') {
      setIsAllSelected(true);
      setSelectedScheduleStatuses([]);
    } else {
      setIsAllSelected(false);
      const newSelected = selectedScheduleStatuses.includes(scheduleStatus)
        ? selectedScheduleStatuses.filter(type => type !== scheduleStatus)
        : [...selectedScheduleStatuses, scheduleStatus];

      setSelectedScheduleStatuses(newSelected);
    }

    setTimeout(() => {
      if (params.api) {
        params.api.onFilterChanged();
      }
    }, 0);
  };

  const handleClear = () => {
    setSelectedScheduleStatuses([]);
    setIsAllSelected(true);
    setSearchText('');
    (window as any).scheduleStatusFilter = [];
    window.dispatchEvent(new CustomEvent('scheduleStatusFilterChanged', {
      detail: { selectedTypes: [] }
    }));
  };

  const isDarkTheme = params.isDarkMode ||
    params.api?.gridOptionsWrapper?.gridOptions?.theme === 'dark' ||
    document.documentElement.classList.contains('dark') ||
    document.body.classList.contains('dark-theme');

  return (
    <div style={{
      padding: '12px',
      minWidth: '240px',
      maxWidth: '260px',
      backgroundColor: isDarkTheme ? '#1e1e1e' : '#ffffff',
      border: `1px solid ${isDarkTheme ? '#333333' : '#d0d7de'}`,
      borderRadius: '6px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: '13px'
    }}>
      <div style={{
        marginBottom: '12px',
        fontWeight: '500',
        color: isDarkTheme ? '#f7fafc' : '#2d3748',
        fontSize: '13px'
      }}>
        Filter Schedule Status
      </div>

      {/* Search Input */}
      <div style={{ marginBottom: '12px' }}>
        <input
          type="text"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{
            width: '100%',
            padding: '6px 8px',
            border: `1px solid ${isDarkTheme ? '#444444' : '#d0d7de'}`,
            borderRadius: '4px',
            backgroundColor: isDarkTheme ? '#2a2a2a' : '#ffffff',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px',
            outline: 'none',
            boxSizing: 'border-box'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#0969da';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = isDarkTheme ? '#444444' : '#d0d7de';
          }}
        />
      </div>

      <div style={{
        maxHeight: '200px',
        overflowY: 'auto',
        marginBottom: '12px'
      }}>
        {/* All option */}
        <div
          key="All"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '6px 8px',
            cursor: 'pointer',
            backgroundColor: isAllSelected ? (isDarkTheme ? '#0969da20' : '#dbeafe') : 'transparent',
            borderRadius: '4px',
            marginBottom: '2px',
            fontWeight: isAllSelected ? '500' : '400',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px'
          }}
          onClick={() => handleScheduleStatusToggle('All')}
        >
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={() => handleScheduleStatusToggle('All')}
            style={{ marginRight: '8px' }}
          />
          <span>All</span>
        </div>

        {filteredScheduleStatuses.length > 0 ? (
          filteredScheduleStatuses.map((scheduleStatus: string) => {
            const isSelected = selectedScheduleStatuses.includes(scheduleStatus);
            return (
              <div
                key={scheduleStatus}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '6px 8px',
                  cursor: 'pointer',
                  backgroundColor: isSelected ? (isDarkTheme ? '#0969da20' : '#dbeafe') : 'transparent',
                  borderRadius: '4px',
                  marginBottom: '2px',
                  color: isDarkTheme ? '#f7fafc' : '#2d3748',
                  fontSize: '13px'
                }}
                onClick={() => handleScheduleStatusToggle(scheduleStatus)}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleScheduleStatusToggle(scheduleStatus)}
                  style={{
                    marginRight: '8px'
                  }}
                />
                <span>{scheduleStatus}</span>
              </div>
            );
          })
        ) : availableScheduleStatuses.length === 0 ? (
          <div style={{ color: isDarkTheme ? '#a0aec0' : '#718096', fontStyle: 'italic' }}>
            Loading schedule statuses...
          </div>
        ) : (
          <div style={{ color: isDarkTheme ? '#a0aec0' : '#718096', fontStyle: 'italic' }}>
            No schedule status found matching
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={handleClear}
          style={{
            padding: '6px 12px',
            backgroundColor: 'transparent',
            border: `1px solid ${isDarkTheme ? '#444444' : '#d0d7de'}`,
            borderRadius: '4px',
            cursor: 'pointer',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px'
          }}
        >
          Clear
        </button>
        <button
          onClick={() => {
            (window as any).scheduleStatusFilter = isAllSelected ? [] : selectedScheduleStatuses;
            window.dispatchEvent(new CustomEvent('scheduleStatusFilterChanged', {
              detail: { selectedTypes: isAllSelected ? [] : selectedScheduleStatuses }
            }));
            params.api?.hidePopupMenu?.();
          }}
          style={{
            padding: '6px 12px',
            backgroundColor: '#0969da',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px'
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );
});

// 🚀 Custom Frequency Filter Component for Schedule Table
const FrequencyFilter = React.forwardRef<any, any>((params, ref) => {
  const [selectedFrequencies, setSelectedFrequencies] = useState<string[]>([]);
  const [availableFrequencies, setAvailableFrequencies] = useState<string[]>(params.frequencies || globalScheduleFrequencies);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');

  const filteredFrequencies = React.useMemo(() => {
    if (!searchText.trim()) return availableFrequencies;
    return availableFrequencies.filter(frequency =>
      frequency.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [availableFrequencies, searchText]);

  React.useEffect(() => {
    const frequenciesToUse = params.frequencies || globalScheduleFrequencies;
    if (frequenciesToUse.length > 0) {
      setAvailableFrequencies([...frequenciesToUse]);
    }
  }, [params.frequencies]);

  React.useImperativeHandle(ref, () => ({
    isFilterActive: () => {
      const isActive = selectedFrequencies.length > 0 && !isAllSelected;
      return isActive;
    },

    doesFilterPass: (params: any) => {
      if (selectedFrequencies.length === 0 || isAllSelected) return true;
      return selectedFrequencies.includes(params.data.frequency);
    },

    getModel: () => {
      return selectedFrequencies.length > 0 && !isAllSelected ? { selectedTypes: selectedFrequencies } : null;
    },

    setModel: (model: any) => {
      if (model && model.selectedTypes) {
        setSelectedFrequencies(model.selectedTypes);
        setIsAllSelected(false);
      } else {
        setSelectedFrequencies([]);
        setIsAllSelected(true);
      }
    },

    afterGuiAttached: () => {
      setAvailableFrequencies([...globalScheduleFrequencies]);
    },

    getGui: () => {
      return null;
    }
  }));

  const handleFrequencyToggle = (frequency: string) => {
    if (frequency === 'All') {
      setIsAllSelected(true);
      setSelectedFrequencies([]);
    } else {
      setIsAllSelected(false);
      const newSelected = selectedFrequencies.includes(frequency)
        ? selectedFrequencies.filter(type => type !== frequency)
        : [...selectedFrequencies, frequency];

      setSelectedFrequencies(newSelected);
    }

    setTimeout(() => {
      if (params.api) {
        params.api.onFilterChanged();
      }
    }, 0);
  };

  const handleClear = () => {
    setSelectedFrequencies([]);
    setIsAllSelected(true);
    setSearchText('');
    (window as any).frequencyFilter = [];
    window.dispatchEvent(new CustomEvent('frequencyFilterChanged', {
      detail: { selectedTypes: [] }
    }));
  };

  const isDarkTheme = params.isDarkMode ||
    params.api?.gridOptionsWrapper?.gridOptions?.theme === 'dark' ||
    document.documentElement.classList.contains('dark') ||
    document.body.classList.contains('dark-theme');

  return (
    <div style={{
      padding: '12px',
      minWidth: '240px',
      maxWidth: '260px',
      backgroundColor: isDarkTheme ? '#1e1e1e' : '#ffffff',
      border: `1px solid ${isDarkTheme ? '#333333' : '#d0d7de'}`,
      borderRadius: '6px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: '13px'
    }}>
      <div style={{
        marginBottom: '12px',
        fontWeight: '500',
        color: isDarkTheme ? '#f7fafc' : '#2d3748',
        fontSize: '13px'
      }}>
        Filter Frequency
      </div>

      <div style={{ marginBottom: '12px' }}>
        <input
          type="text"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{
            width: '100%',
            padding: '6px 8px',
            border: `1px solid ${isDarkTheme ? '#444444' : '#d0d7de'}`,
            borderRadius: '4px',
            backgroundColor: isDarkTheme ? '#2a2a2a' : '#ffffff',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px',
            outline: 'none',
            boxSizing: 'border-box'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#0969da';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = isDarkTheme ? '#444444' : '#d0d7de';
          }}
        />
      </div>

      <div style={{
        maxHeight: '200px',
        overflowY: 'auto',
        marginBottom: '12px'
      }}>
        <div
          key="All"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '6px 8px',
            cursor: 'pointer',
            backgroundColor: isAllSelected ? (isDarkTheme ? '#0969da20' : '#dbeafe') : 'transparent',
            borderRadius: '4px',
            marginBottom: '2px',
            fontWeight: isAllSelected ? '500' : '400',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px'
          }}
          onClick={() => handleFrequencyToggle('All')}
        >
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={() => handleFrequencyToggle('All')}
            style={{ marginRight: '8px' }}
          />
          <span>All</span>
        </div>

        {filteredFrequencies.length > 0 ? (
          filteredFrequencies.map((frequency: string) => {
            const isSelected = selectedFrequencies.includes(frequency);
            return (
              <div
                key={frequency}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '6px 8px',
                  cursor: 'pointer',
                  backgroundColor: isSelected ? (isDarkTheme ? '#0969da20' : '#dbeafe') : 'transparent',
                  borderRadius: '4px',
                  marginBottom: '2px',
                  color: isDarkTheme ? '#f7fafc' : '#2d3748',
                  fontSize: '13px'
                }}
                onClick={() => handleFrequencyToggle(frequency)}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleFrequencyToggle(frequency)}
                  style={{
                    marginRight: '8px'
                  }}
                />
                <span>{frequency}</span>
              </div>
            );
          })
        ) : availableFrequencies.length === 0 ? (
          <div style={{ color: isDarkTheme ? '#a0aec0' : '#718096', fontStyle: 'italic' }}>
            Loading frequencies...
          </div>
        ) : (
          <div style={{ color: isDarkTheme ? '#a0aec0' : '#718096', fontStyle: 'italic' }}>
            No frequencies found matching
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={handleClear}
          style={{
            padding: '6px 12px',
            backgroundColor: 'transparent',
            border: `1px solid ${isDarkTheme ? '#444444' : '#d0d7de'}`,
            borderRadius: '4px',
            cursor: 'pointer',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px'
          }}
        >
          Clear
        </button>
        <button
          onClick={() => {
            (window as any).frequencyFilter = isAllSelected ? [] : selectedFrequencies;
            window.dispatchEvent(new CustomEvent('frequencyFilterChanged', {
              detail: { selectedTypes: isAllSelected ? [] : selectedFrequencies }
            }));
            params.api?.hidePopupMenu?.();
          }}
          style={{
            padding: '6px 12px',
            backgroundColor: '#0969da',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px'
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );
});

// 🚀 Custom CreatedBy Filter Component for Schedule Table
const CreatedByFilter = React.forwardRef<any, any>((params, ref) => {
  const [selectedCreatedBy, setSelectedCreatedBy] = useState<string[]>([]);
  const [availableCreatedBy, setAvailableCreatedBy] = useState<string[]>(params.createdBy || globalScheduleCreatedBy);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');

  const filteredCreatedBy = React.useMemo(() => {
    if (!searchText.trim()) return availableCreatedBy;
    return availableCreatedBy.filter(creator =>
      creator.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [availableCreatedBy, searchText]);

  React.useEffect(() => {
    const createdByToUse = params.createdBy || globalScheduleCreatedBy;
    if (createdByToUse.length > 0) {
      setAvailableCreatedBy([...createdByToUse]);
    }
  }, [params.createdBy]);

  React.useImperativeHandle(ref, () => ({
    isFilterActive: () => {
      const isActive = selectedCreatedBy.length > 0 && !isAllSelected;
      return isActive;
    },

    doesFilterPass: (params: any) => {
      if (selectedCreatedBy.length === 0 || isAllSelected) return true;
      return selectedCreatedBy.includes(params.data.createdBy);
    },

    getModel: () => {
      return selectedCreatedBy.length > 0 && !isAllSelected ? { selectedTypes: selectedCreatedBy } : null;
    },

    setModel: (model: any) => {
      if (model && model.selectedTypes) {
        setSelectedCreatedBy(model.selectedTypes);
        setIsAllSelected(false);
      } else {
        setSelectedCreatedBy([]);
        setIsAllSelected(true);
      }
    },

    afterGuiAttached: () => {
      setAvailableCreatedBy([...globalScheduleCreatedBy]);
    },

    getGui: () => {
      return null;
    }
  }));

  const handleCreatedByToggle = (creator: string) => {
    if (creator === 'All') {
      setIsAllSelected(true);
      setSelectedCreatedBy([]);
    } else {
      setIsAllSelected(false);
      const newSelected = selectedCreatedBy.includes(creator)
        ? selectedCreatedBy.filter(type => type !== creator)
        : [...selectedCreatedBy, creator];

      setSelectedCreatedBy(newSelected);
    }

    setTimeout(() => {
      if (params.api) {
        params.api.onFilterChanged();
      }
    }, 0);
  };

  const handleClear = () => {
    setSelectedCreatedBy([]);
    setIsAllSelected(true);
    setSearchText('');
    (window as any).createdByFilter = [];
    window.dispatchEvent(new CustomEvent('createdByFilterChanged', {
      detail: { selectedTypes: [] }
    }));
  };

  const isDarkTheme = params.isDarkMode ||
    params.api?.gridOptionsWrapper?.gridOptions?.theme === 'dark' ||
    document.documentElement.classList.contains('dark') ||
    document.body.classList.contains('dark-theme');

  return (
    <div style={{
      padding: '12px',
      minWidth: '240px',
      backgroundColor: isDarkTheme ? '#1e1e1e' : '#ffffff',
      border: `1px solid ${isDarkTheme ? '#333333' : '#d0d7de'}`,
      borderRadius: '6px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: '13px'
    }}>
      <div style={{
        marginBottom: '12px',
        fontWeight: '500',
        color: isDarkTheme ? '#f7fafc' : '#2d3748',
        fontSize: '13px'
      }}>
        Filter Created By
      </div>

      <div style={{ marginBottom: '12px' }}>
        <input
          type="text"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{
            width: '100%',
            padding: '6px 8px',
            border: `1px solid ${isDarkTheme ? '#444444' : '#d0d7de'}`,
            borderRadius: '4px',
            backgroundColor: isDarkTheme ? '#2a2a2a' : '#ffffff',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px',
            outline: 'none',
            boxSizing: 'border-box'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#0969da';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = isDarkTheme ? '#444444' : '#d0d7de';
          }}
        />
      </div>

      <div style={{
        maxHeight: '200px',
        overflowY: 'auto',
        marginBottom: '12px'
      }}>
        <div
          key="All"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '6px 8px',
            cursor: 'pointer',
            backgroundColor: isAllSelected ? (isDarkTheme ? '#0969da20' : '#dbeafe') : 'transparent',
            borderRadius: '4px',
            marginBottom: '2px',
            fontWeight: isAllSelected ? '500' : '400',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px'
          }}
          onClick={() => handleCreatedByToggle('All')}
        >
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={() => handleCreatedByToggle('All')}
            style={{ marginRight: '8px' }}
          />
          <span>All</span>
        </div>

        {filteredCreatedBy.length > 0 ? (
          filteredCreatedBy.map((creator: string) => {
            const isSelected = selectedCreatedBy.includes(creator);
            return (
              <div
                key={creator}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '6px 8px',
                  cursor: 'pointer',
                  backgroundColor: isSelected ? (isDarkTheme ? '#0969da20' : '#dbeafe') : 'transparent',
                  borderRadius: '4px',
                  marginBottom: '2px',
                  color: isDarkTheme ? '#f7fafc' : '#2d3748',
                  fontSize: '13px'
                }}
                onClick={() => handleCreatedByToggle(creator)}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleCreatedByToggle(creator)}
                  style={{
                    marginRight: '8px'
                  }}
                />
                <span>{creator}</span>
              </div>
            );
          })
        ) : availableCreatedBy.length === 0 ? (
          <div style={{ color: isDarkTheme ? '#a0aec0' : '#718096', fontStyle: 'italic' }}>
            Loading creators...
          </div>
        ) : (
          <div style={{ color: isDarkTheme ? '#a0aec0' : '#718096', fontStyle: 'italic' }}>
            No creators found matching
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={handleClear}
          style={{
            padding: '6px 12px',
            backgroundColor: 'transparent',
            border: `1px solid ${isDarkTheme ? '#444444' : '#d0d7de'}`,
            borderRadius: '4px',
            cursor: 'pointer',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px'
          }}
        >
          Clear
        </button>
        <button
          onClick={() => {
            (window as any).createdByFilter = isAllSelected ? [] : selectedCreatedBy;
            window.dispatchEvent(new CustomEvent('createdByFilterChanged', {
              detail: { selectedTypes: isAllSelected ? [] : selectedCreatedBy }
            }));
            params.api?.hidePopupMenu?.();
          }}
          style={{
            padding: '6px 12px',
            backgroundColor: '#0969da',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px'
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );
});

// 🚀 Custom ScheduledStatus Filter Component for Schedule Table
const ScheduledStatusFilter = React.forwardRef<any, any>((params, ref) => {
  const [selectedScheduledStatuses, setSelectedScheduledStatuses] = useState<string[]>([]);
  const [availableScheduledStatuses, setAvailableScheduledStatuses] = useState<string[]>(params.scheduledStatuses || globalScheduleScheduledStatuses);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');

  const filteredScheduledStatuses = React.useMemo(() => {
    if (!searchText.trim()) return availableScheduledStatuses;
    return availableScheduledStatuses.filter(status =>
      status.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [availableScheduledStatuses, searchText]);

  React.useEffect(() => {
    const statusesToUse = params.scheduledStatuses || globalScheduleScheduledStatuses;
    if (statusesToUse.length > 0) {
      setAvailableScheduledStatuses([...statusesToUse]);
    }
  }, [params.scheduledStatuses]);

  React.useImperativeHandle(ref, () => ({
    isFilterActive: () => {
      const isActive = selectedScheduledStatuses.length > 0 && !isAllSelected;
      return isActive;
    },

    doesFilterPass: (params: any) => {
      if (selectedScheduledStatuses.length === 0 || isAllSelected) return true;
      return selectedScheduledStatuses.includes(params.data.scheduledStatus);
    },

    getModel: () => {
      return selectedScheduledStatuses.length > 0 && !isAllSelected ? { selectedTypes: selectedScheduledStatuses } : null;
    },

    setModel: (model: any) => {
      if (model && model.selectedTypes) {
        setSelectedScheduledStatuses(model.selectedTypes);
        setIsAllSelected(false);
      } else {
        setSelectedScheduledStatuses([]);
        setIsAllSelected(true);
      }
    },

    afterGuiAttached: () => {
      setAvailableScheduledStatuses([...globalScheduleScheduledStatuses]);
    },

    getGui: () => {
      return null;
    }
  }));

  const handleScheduledStatusToggle = (status: string) => {
    if (status === 'All') {
      setIsAllSelected(true);
      setSelectedScheduledStatuses([]);
    } else {
      setIsAllSelected(false);
      const newSelected = selectedScheduledStatuses.includes(status)
        ? selectedScheduledStatuses.filter(type => type !== status)
        : [...selectedScheduledStatuses, status];

      setSelectedScheduledStatuses(newSelected);
    }

    setTimeout(() => {
      if (params.api) {
        params.api.onFilterChanged();
      }
    }, 0);
  };

  const handleClear = () => {
    setSelectedScheduledStatuses([]);
    setIsAllSelected(true);
    setSearchText('');
    (window as any).scheduledStatusFilter = [];
    window.dispatchEvent(new CustomEvent('scheduledStatusFilterChanged', {
      detail: { selectedTypes: [] }
    }));
  };

  const isDarkTheme = params.isDarkMode ||
    params.api?.gridOptionsWrapper?.gridOptions?.theme === 'dark' ||
    document.documentElement.classList.contains('dark') ||
    document.body.classList.contains('dark-theme');

  return (
    <div style={{
      padding: '12px',
      minWidth: '240px',
      backgroundColor: isDarkTheme ? '#1e1e1e' : '#ffffff',
      maxWidth: '260px',
      border: `1px solid ${isDarkTheme ? '#333333' : '#d0d7de'}`,
      borderRadius: '6px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: '13px'
    }}>
      <div style={{
        marginBottom: '12px',
        fontWeight: '500',
        color: isDarkTheme ? '#f7fafc' : '#2d3748',
        fontSize: '13px'
      }}>
        Filter Scheduled Status
      </div>

      <div style={{ marginBottom: '12px' }}>
        <input
          type="text"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{
            width: '100%',
            padding: '6px 8px',
            border: `1px solid ${isDarkTheme ? '#444444' : '#d0d7de'}`,
            borderRadius: '4px',
            backgroundColor: isDarkTheme ? '#2a2a2a' : '#ffffff',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px',
            outline: 'none',
            boxSizing: 'border-box'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#0969da';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = isDarkTheme ? '#444444' : '#d0d7de';
          }}
        />
      </div>

      <div style={{
        maxHeight: '200px',
        overflowY: 'auto',
        marginBottom: '12px'
      }}>
        <div
          key="All"
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '6px 8px',
            cursor: 'pointer',
            backgroundColor: isAllSelected ? (isDarkTheme ? '#0969da20' : '#dbeafe') : 'transparent',
            borderRadius: '4px',
            marginBottom: '2px',
            fontWeight: isAllSelected ? '500' : '400',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px'
          }}
          onClick={() => handleScheduledStatusToggle('All')}
        >
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={() => handleScheduledStatusToggle('All')}
            style={{ marginRight: '8px' }}
          />
          <span>All</span>
        </div>

        {filteredScheduledStatuses.length > 0 ? (
          filteredScheduledStatuses.map((status: string) => {
            const isSelected = selectedScheduledStatuses.includes(status);
            return (
              <div
                key={status}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '6px 8px',
                  cursor: 'pointer',
                  backgroundColor: isSelected ? (isDarkTheme ? '#0969da20' : '#dbeafe') : 'transparent',
                  borderRadius: '4px',
                  marginBottom: '2px',
                  color: isDarkTheme ? '#f7fafc' : '#2d3748',
                  fontSize: '13px'
                }}
                onClick={() => handleScheduledStatusToggle(status)}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleScheduledStatusToggle(status)}
                  style={{
                    marginRight: '8px'
                  }}
                />
                <span>{status}</span>
              </div>
            );
          })
        ) : availableScheduledStatuses.length === 0 ? (
          <div style={{ color: isDarkTheme ? '#a0aec0' : '#718096', fontStyle: 'italic' }}>
            Loading statuses...
          </div>
        ) : (
          <div style={{ color: isDarkTheme ? '#a0aec0' : '#718096', fontStyle: 'italic' }}>
            No statuses found matching
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={handleClear}
          style={{
            padding: '6px 12px',
            backgroundColor: 'transparent',
            border: `1px solid ${isDarkTheme ? '#444444' : '#d0d7de'}`,
            borderRadius: '4px',
            cursor: 'pointer',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px'
          }}
        >
          Clear
        </button>
        <button
          onClick={() => {
            (window as any).scheduledStatusFilter = isAllSelected ? [] : selectedScheduledStatuses;
            window.dispatchEvent(new CustomEvent('scheduledStatusFilterChanged', {
              detail: { selectedTypes: isAllSelected ? [] : selectedScheduledStatuses }
            }));
            params.api?.hidePopupMenu?.();
          }}
          style={{
            padding: '6px 12px',
            backgroundColor: '#0969da',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px'
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );
});

// 🚀 Custom ScanName Filter Component for Schedule Table
const ScanNameFilter = React.forwardRef<any, any>((params, ref) => {
  const [selectedScanNames, setSelectedScanNames] = useState<string[]>([]);
  const [availableScanNames, setAvailableScanNames] = useState<string[]>(params.scanNames || globalScheduleScanNames);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [filteredScanNames, setFilteredScanNames] = useState<string[]>([]);

  React.useEffect(() => {
    const scanNamesToUse = params.scanNames || globalScheduleScanNames;
    if (scanNamesToUse.length > 0) {
      setAvailableScanNames([...scanNamesToUse]);
      setFilteredScanNames([...scanNamesToUse]);
    }
  }, [params.scanNames]);

  // Filter scan names based on search text
  React.useEffect(() => {
    if (!searchText.trim()) {
      setFilteredScanNames([...availableScanNames]);
    } else {
      const filtered = availableScanNames.filter(scanName =>
        scanName.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredScanNames(filtered);
    }
  }, [searchText, availableScanNames]);

  React.useImperativeHandle(ref, () => ({
    isFilterActive: () => {
      const isActive = selectedScanNames.length > 0 && !isAllSelected;
      return isActive;
    },

    doesFilterPass: (params: any) => {
      if (selectedScanNames.length === 0 || isAllSelected) return true;
      // Support both scanName and schedule_scan_id filtering
      return selectedScanNames.includes(params.data.scanName);
    },

    getModel: () => {
      return selectedScanNames.length > 0 && !isAllSelected ? { selectedTypes: selectedScanNames } : null;
    },

    setModel: (model: any) => {
      if (model && model.selectedTypes) {
        setSelectedScanNames(model.selectedTypes);
        setIsAllSelected(false);
      } else {
        setSelectedScanNames([]);
        setIsAllSelected(true);
      }
    },

    afterGuiAttached: () => {
      setAvailableScanNames([...globalScheduleScanNames]);
      setFilteredScanNames([...globalScheduleScanNames]);
    },

    getGui: () => {
      return null;
    }
  }));

  const handleScanNameToggle = (scanName: string) => {
    setIsAllSelected(false);
    const newSelected = selectedScanNames.includes(scanName)
      ? selectedScanNames.filter(name => name !== scanName)
      : [...selectedScanNames, scanName];

    setSelectedScanNames(newSelected);
  };

  const handleApply = () => {
    setTimeout(() => {
      if (params.api) {
        params.api.onFilterChanged();
      }
    }, 0);
  };

  // also close the popup when Apply is clicked
  const handleApplyWithClose = () => {
    setTimeout(() => {
      if (params.api) {
        params.api.onFilterChanged();
        params.api.hidePopupMenu?.();
      }
    }, 0);
  };

  const handleClear = () => {
    setSelectedScanNames([]);
    setIsAllSelected(true);
    setSearchText('');
    (window as any).scanNameFilter = [];
    window.dispatchEvent(new CustomEvent('scanNameFilterChanged', {
      detail: { selectedTypes: [] }
    }));
    setTimeout(() => {
      if (params.api) {
        params.api.onFilterChanged();
      }
    }, 0);
  };

  const isDarkTheme = params.isDarkMode ||
    params.api?.gridOptionsWrapper?.gridOptions?.theme === 'dark' ||
    document.documentElement.classList.contains('dark') ||
    document.body.classList.contains('dark-theme');

  return (
    <div style={{
      padding: '12px',
      minWidth: '240px',
      maxHeight: '400px',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: isDarkTheme ? '#1e1e1e' : '#ffffff',
      border: `1px solid ${isDarkTheme ? '#333333' : '#d0d7de'}`,
      borderRadius: '6px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: '13px'
    }}>
      <div style={{
        marginBottom: '12px',
        fontWeight: '500',
        color: isDarkTheme ? '#f7fafc' : '#2d3748',
        fontSize: '13px'
      }}>
        Filter Scan Name
      </div>

      <div style={{ marginBottom: '12px' }}>
        <input
          type="text"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{
            width: '100%',
            padding: '6px 8px',
            border: `1px solid ${isDarkTheme ? '#444444' : '#d0d7de'}`,
            borderRadius: '4px',
            backgroundColor: isDarkTheme ? '#2a2a2a' : '#ffffff',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px',
            outline: 'none',
            boxSizing: 'border-box'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#0969da';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = isDarkTheme ? '#444444' : '#d0d7de';
          }}
        />
      </div>

      {/* Filtered results list */}
      {filteredScanNames.length > 0 && (
        <div style={{
          maxHeight: '200px',
          overflowY: 'auto',
          marginBottom: '12px',
          border: `1px solid ${isDarkTheme ? '#333333' : '#e1e4e8'}`,
          borderRadius: '4px',
          backgroundColor: isDarkTheme ? '#2a2a2a' : '#f6f8fa'
        }}>
          {filteredScanNames.map((scanName, index) => (
            <div
              key={index}
              style={{
                padding: '6px 8px',
                borderBottom: index < filteredScanNames.length - 1 ? `1px solid ${isDarkTheme ? '#333333' : '#e1e4e8'}` : 'none',
                cursor: 'pointer',
                backgroundColor: selectedScanNames.includes(scanName)
                  ? (isDarkTheme ? '#0969da' : '#0366d6')
                  : 'transparent',
                color: selectedScanNames.includes(scanName)
                  ? '#ffffff'
                  : isDarkTheme ? '#f7fafc' : '#2d3748',
                fontSize: '12px'
              }}
              onClick={() => handleScanNameToggle(scanName)}
              onMouseEnter={(e) => {
                if (!selectedScanNames.includes(scanName)) {
                  e.currentTarget.style.backgroundColor = isDarkTheme ? '#444444' : '#f1f8ff';
                }
              }}
              onMouseLeave={(e) => {
                if (!selectedScanNames.includes(scanName)) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              {scanName}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={handleClear}
          style={{
            flex: 1,
            padding: '6px 12px',
            backgroundColor: 'transparent',
            border: `1px solid ${isDarkTheme ? '#444444' : '#d0d7de'}`,
            borderRadius: '4px',
            cursor: 'pointer',
            color: isDarkTheme ? '#f7fafc' : '#2d3748',
            fontSize: '13px'
          }}
        >
          Clear
        </button>
        <button
          onClick={handleApplyWithClose}
          style={{
            flex: 1,
            padding: '6px 12px',
            backgroundColor: '#0969da',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px'
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );
});

const ActionMenu = ({
  params,
  module,
  refetch,
}: {
  params: ICellRendererParams;
  module: string;
  refetch: any;
}) => {
  console.log("params", params.data);
  const dispatch = useDispatch();
  const { getCookie } = useStorageManager();
  const isAdmin = getCookie("is_admin") === "true";
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [rescanDialogOpen, setRescanDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { hasPermissions } = useRolePermissions();
  const [loading, setloading] = useState(false)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRescan = (action: string) => {
    setRescanAction(action);
    setRescanDialogOpen(true);

    handleClose();
  };

  const handleRescanConfirm = async (action: any) => {
    console.log("action", action);
    console.log("params.data", params.data);
    setloading(true)
    let url = END_POINTS.ON_PREM_MY_SCAN_RESCAN;
    let payload: any = {
      scan_id: params.data.scan_uuid,
      scan_source: "Scans",
    };
    if (action === "cancel") {
      payload = {
        scanid: params.data.scan_uuid,
        scanIdNo: params.data.scanName,
        cancel_rescan: "cancel",
      };
      url = "securityscan/cancel-rescan";
    }
    try {
      console.log("action", action);
      //params.data
      const response = await axiosInstance.post(url, payload);
      if (response.status === 200) {
        refetch();
        toast.success(response.data.message || "Rescan initiated successfully");
      }
      setRescanDialogOpen(false);
      setloading(false);
    } catch (error) {
      toast.error("Failed to initiate rescan");
      setRescanDialogOpen(false);
      setloading(false);
    }
  };

  const handleRescanDialogClose = () => {
    setRescanDialogOpen(false);
  };

  const handleRename = (data: any) => {
    dispatch(
      myScanActions.renamePanel({
        isRenamePanelOpen: true,
        scan_id: data.scan_uuid,
        scanName: data.scanName,
      })
    );
    handleClose();
  };

  const handleSchedule = (data: any) => {
    console.log("data", data);
    const isSchedulable = String(data.schedule_status ?? "").toLowerCase() === "schedulable";
    dispatch(
      myScanActions.changeSchedule({
        isSchedulePanelOpen: true,
        scan_id: data.scan_uuid,
        scanName: data.scanName,
        schedule_id: isSchedulable ? "" : data.scheduleId,
        frequency_id: data.schedule?.frequency_id || "",
        atTime: data.schedule?.values?.time || "",
        date: data.schedule?.values?.date || "",
        day: data.schedule?.values?.day || "",
        type: data.type || "",
        ip_range: data.ip_range,
      })
    );
    handleClose();
  };

  const handleScanInfoClick = (data: any) => {
    console.log("data", data)
    dispatch(setEdit({ edit: true, scan_uuid: data.scan_uuid, status: data.statusTemp, scanTime: data.scanTime }));

    //dispatch(myScanActions.scanInformaton({ scan_id: data.scan_uuid,statusTemp: data.statusTemp }))
    hasPermissions(ROLES_CONTENT_PROPS.SCAN_DETAILS) ? navigate(`/on-premises/scan-details/${data.scan_uuid}`) : toast.error("You do not have permission to view scan details");
    handleClose()
  }

  const [rescanAction, setRescanAction] = useState(""); // e.g., "Rescan" or "Cancel"

  return (
    <>
      {isAdmin && (
        <IconButton
          size="small"
          onClick={handleClick}
          sx={{ "&:focus": { outline: "none" } }}
        >
          <MoreVertIcon />
        </IconButton>
      )}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem
          onClick={() => {
            handleRename(params.data);
          }}
          sx={{ fontSize: "14px" }}
        >
          Rename
        </MenuItem>
        {params.data.status !== "Cancelled" && module == "onpremises" && (
          <MenuItem
            onClick={() => {
              handleScanInfoClick(params.data);
            }}
            sx={{ fontSize: "14px" }}
          >
            Scan Information
          </MenuItem>
        )}
        {params.data.schedule_status === "schedulable" &&
          (hasPermissions(ROLES_CONTENT_PROPS.EDIT_SCHEDULE) ||
            hasPermissions(ROLES_CONTENT_PROPS.SCHEDULE)) && (
            <MenuItem
              onClick={() => {
                handleSchedule(params.data);
              }}
              sx={{ fontSize: "14px" }}
            >
              Schedule Scan
            </MenuItem>
          )}
        {params.data.schedule_status &&
          params.data.schedule_status == "scheduled" &&
          (hasPermissions(ROLES_CONTENT_PROPS.EDIT_SCHEDULE) ||
            hasPermissions(ROLES_CONTENT_PROPS.SCHEDULE)) && (
            <MenuItem
              onClick={() => {
                handleSchedule(params.data);
              }}
              sx={{ fontSize: "14px" }}
            >
              Change Schedule
            </MenuItem>
          )}
        {params.data.reScan && (
          <MenuItem
            onClick={() => handleRescan("rescan")}
            sx={{ fontSize: "14px" }}
          >
            Rescan
          </MenuItem>
        )}
        {params.data.statusTemp &&
          (params.data.statusTemp == 1 || params.data.statusTemp == 2) && (
            <MenuItem
              onClick={() => handleRescan("cancel")}
              sx={{ fontSize: "14px" }}
            >
              Cancel
            </MenuItem>
          )}
      </Menu>
      <RescanConfirmDialog
        open={rescanDialogOpen}
        onClose={handleRescanDialogClose}
        onConfirm={handleRescanConfirm}
        scanName={params.data.scanName}
        action={rescanAction}
        loading={loading}
      />
    </>
  );
};

const MyScanTable = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const isScheduleView = searchParams.get("view") === "schedule";
  const [isChecked, setIsChecked] = useState(isScheduleView);
  const [scanRowData, setScanRowData] = useState([]);
  const [scheduleRowData, setScheduleRowData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const renameDetails = useSelector((state: any) => state.onPremMyScan);
  // console.log("renameDetails", renameDetails);
  const { hasPermissions } = useRolePermissions();

  // 🚀 Generic Filter System - State for unique values and active filters
  const [uniqueValues, setUniqueValues] = useState<Record<string, string[]>>({
    status: [],
    user: [],
    schedule_status: []
  });

  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({
    status: [],
    user: [],
    schedule_status: []
  });

  // 🚀 Schedule Table unique values state
  const [scheduleUniqueValues, setScheduleUniqueValues] = useState<Record<string, string[]>>({
    frequency: [],
    createdBy: [],
    scheduledStatus: [],
    scanName: []
  });

  const [scheduleActiveFilters, setScheduleActiveFilters] = useState<Record<string, string[]>>({
    frequency: [],
    createdBy: [],
    scheduledStatus: [],
    scanName: []
  });

  // 🚀 Generic filter change handler
  const handleFilterChange = (filterType: string, selectedValues: string[]) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterType]: selectedValues
    }));
  };

  // 🚀 Generic filter change handler for schedule filters
  const handleScheduleFilterChange = (filterType: string, selectedValues: string[]) => {
    setScheduleActiveFilters(prev => ({
      ...prev,
      [filterType]: selectedValues
    }));
  };

  // 🚀 Listen for filter changes from custom filter components
  useEffect(() => {
    const handleStatusFilterChange = (event: any) => {
      handleFilterChange('status', event.detail.selectedTypes);
    };

    const handleUserFilterChange = (event: any) => {
      handleFilterChange('user', event.detail.selectedTypes);
    };

    const handleScheduleStatusFilterChange = (event: any) => {
      handleFilterChange('schedule_status', event.detail.selectedTypes);
    };

    // Schedule table filters
    const handleFrequencyFilterChange = (event: any) => {
      handleScheduleFilterChange('frequency', event.detail.selectedTypes);
    };

    const handleCreatedByFilterChange = (event: any) => {
      handleScheduleFilterChange('createdBy', event.detail.selectedTypes);
    };

    const handleScheduledStatusFilterChange = (event: any) => {
      handleScheduleFilterChange('scheduledStatus', event.detail.selectedTypes);
    };

    const handleScanNameFilterChange = (event: any) => {
      handleScheduleFilterChange('scanName', event.detail.selectedTypes);
    };

    window.addEventListener('statusFilterChanged', handleStatusFilterChange);
    window.addEventListener('userFilterChanged', handleUserFilterChange);
    window.addEventListener('scheduleStatusFilterChanged', handleScheduleStatusFilterChange);
    window.addEventListener('frequencyFilterChanged', handleFrequencyFilterChange);
    window.addEventListener('createdByFilterChanged', handleCreatedByFilterChange);
    window.addEventListener('scheduledStatusFilterChanged', handleScheduledStatusFilterChange);
    window.addEventListener('scanNameFilterChanged', handleScanNameFilterChange);

    return () => {
      window.removeEventListener('statusFilterChanged', handleStatusFilterChange);
      window.removeEventListener('userFilterChanged', handleUserFilterChange);
      window.removeEventListener('scheduleStatusFilterChanged', handleScheduleStatusFilterChange);
      window.removeEventListener('frequencyFilterChanged', handleFrequencyFilterChange);
      window.removeEventListener('createdByFilterChanged', handleCreatedByFilterChange);
      window.removeEventListener('scheduledStatusFilterChanged', handleScheduledStatusFilterChange);
      window.removeEventListener('scanNameFilterChanged', handleScanNameFilterChange);
    };
  }, []);

  let url = "";
  //const location = useLocation();
  let module = "";

  if (location.pathname == "/on-premises/scans") {
    url = END_POINTS.ON_PREM_MY_SCAN;
    module = "onpremises";
  } else {
    module = "cloud";
    url = END_POINTS.CLOUD_SCANS;
  }
  //on-premises/scans

  // Inside your component
  const myscanSocketData = useSelector(
    (state: RootState) => state.MyScanDetails.myscanSocketData
  );
  // const myscanSocketData :any= [];

  //    useEffect(() => {
  //     if (!myscanSocketData || myscanSocketData.length === 0) return;

  //     setScanRowData((prevData:any) => {
  //       const updatedData:any = [...prevData];

  //       myscanSocketData.forEach((incomingRow) => {
  //         const existingIndex = updatedData.findIndex(
  //           (row:any) => row.scan_uuid === incomingRow.scan_uuid
  //         );

  //         console.log("existingIndex",existingIndex)
  //         console.log("incomingRow",incomingRow)
  //         console.log("updatedData",updatedData)

  //         if (existingIndex !== -1) {
  //           // Always update all fields, including new ones
  //           updatedData[existingIndex] = {
  //             ...updatedData[existingIndex],
  //             ...incomingRow,
  //           };
  //           console.log("updatedData[existingIndex]",updatedData[existingIndex])
  //         } else {
  //           // New row: add to top
  //           updatedData.unshift(incomingRow);
  //         }
  //       });

  //       return updatedData;
  //     });
  //   }, [myscanSocketData]);

  const assetCount = async () => {
    console.log("assetCount called");
    dispatch(resetUserTypeFlags());


    const res = await axiosInstance.get(END_POINTS.IS_NEW_USER);
    let parsed = null;
    if (res.status === 200) {
      dispatch(setIsOnlyPremAssetUser({ isOnlyPremAssetUser: false }));
      dispatch(setIsOnlyCloudUser({ isOnlyCloudUser: false }));
      dispatch(setIsBothCloudAndPremAssetUser({ isBothCloudAndPremAssetUser: false }));
      parsed = res?.data?.data || false;
      if (parsed.device_count == 0 && parsed.cloud_count == 0) {
        dispatch(setIsNewUserTrue({ isNewUserTrue: true }));
        //dispatch(setIsOnlyCloudUser({ is: true }));

      }
      else if (parsed.device_count > 0 && parsed.cloud_count == 0) {
        dispatch(setIsOnlyPremAssetUser({ isOnlyPremAssetUser: true }));
      } else if (parsed.device_count == 0 && parsed.cloud_count > 0) {
        dispatch(setIsOnlyCloudUser({ isOnlyCloudUser: true }));
      } else if (parsed.device_count > 0 && parsed.cloud_count > 0) {
        dispatch(setIsBothCloudAndPremAssetUser({ isBothCloudAndPremAssetUser: true }));
      }
    }
  }

  useEffect(() => {
    const updateScanData = async () => {
      if (!myscanSocketData || myscanSocketData.length === 0) return;

      setScanRowData((prevData: any) => {
        const updatedData: any = [...prevData];

        myscanSocketData.forEach((incomingRow: any) => {
          console.log("incomingRow", incomingRow)
          console.log("[Socket] incomingRow raw data:", incomingRow);
          console.log("[Socket] schedule_status from socket:", incomingRow.schedule_status);
          const mappedRow = {
            id: incomingRow.id ?? 0,
            scanName: incomingRow.scanname,
            scanTime: incomingRow.date_time,
            status: getStatus(incomingRow.status),
            statusTemp: incomingRow.status,
            assets: `${incomingRow.passed_count}/${incomingRow.total_count}`,
            // assets:incomingRow?.numberofdevices,
            securityScore: incomingRow.securityrating,
            misconfiguration: incomingRow.vulnerabilities,
            user: incomingRow.user,
            scan_uuid: incomingRow.scan_uuid,
            onprem_posture_id: incomingRow.onprem_posture_id || incomingRow.scan_summary_id,
            schedule: incomingRow.schedule
              ? {
                ...incomingRow.schedule,
                values: {
                  ...incomingRow.schedule.values,
                  time: incomingRow.schedule.values?.time
                    ? moment.utc(incomingRow.schedule.values.time, "hh:mm A").local().format("hh:mm A")
                    : incomingRow.schedule.values?.time,
                },
              }
              : null,
            reScan: incomingRow.rescan ?? false,
            scheduleId: incomingRow.schedule_id ?? null,
            schedule_status: incomingRow.schedule_status ?? null,
            ip_range: incomingRow.ip_range || incomingRow.ipRange || null,
          };

          const existingIndex = updatedData.findIndex(
            (row: any) => row.scan_uuid === incomingRow.scan_uuid
          );

          if (existingIndex !== -1) {
            const existingRow = updatedData[existingIndex];
            updatedData[existingIndex] = {
              ...existingRow,
              ...mappedRow,
              // Safely preserve fields the socket may not include
              schedule_status: incomingRow.schedule_status ?? existingRow.schedule_status ?? null,
              schedule: mappedRow.schedule ?? existingRow.schedule ?? null,
              scheduleId: mappedRow.scheduleId ?? existingRow.scheduleId ?? null,
              ip_range: incomingRow.ip_range ?? incomingRow.ipRange ?? existingRow.ip_range ?? null,
            };
          } else {
            updatedData.unshift(mappedRow);
          }
        });

        return updatedData;
      });
      if (myscanSocketData.some((item: any) => item.status === 3)) {
        console.log("welcome");
        await assetCount();
      }
    };

    updateScanData();
  }, [myscanSocketData]);

  const payload = {
    timezone: "Asia/Calcutta",
    filter_sched: [],
    search: "",
  };

  const fetchScanData = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post(url, payload);
      if (response.data?.data) {
        dispatch(setScanDetails(response?.data?.data));

        const mappedData = response.data.data.map((scan: any, index: any) => ({
          id: index + 1,
          scanName: scan.scanname,
          scanTime: scan.date_time,
          status: getStatus(scan.status),
          statusTemp: scan.status,
          assets: module === "onpremises" ? `${scan.passed_count}/${scan.total_count}` : scan?.numberofdevices,
          // assets:scan?.numberofdevices,
          securityScore: scan.securityrating,
          score: Math.round(scan.securityrating),
          misconfiguration: scan.vulnerabilities,
          user: module == 'onpremises' ? scan.user : scan.user_name,
          scan_uuid: scan.scan_uuid,
          onprem_posture_id: scan.onprem_posture_id || scan.scan_summary_id,
          schedule: scan.schedule,
          reScan: scan.rescan,
          scheduleId: scan.schedule_id,
          schedule_status: scan.schedule_status,
          type: scan.type || scan.scan_type || "",
          ip_range: scan.ip_range,
        }))
          .sort((a: any, b: any) => new Date(b.scanTime).getTime() - new Date(a.scanTime).getTime()); // Descending sort by date

        // 🚀 Extract unique values for custom filters
        const statuses = Array.from(
          new Set(
            mappedData
              .map((scan: any) => scan.status)
              .filter((status: string) => status && status.trim() !== "")
          )
        ) as string[];

        const users = Array.from(
          new Set(
            mappedData
              .map((scan: any) => scan.user)
              .filter((user: string) => user && user.trim() !== "")
          )
        ) as string[];

        const scheduleStatuses = Array.from(
          new Set(
            mappedData
              .map((scan: any) => scan.schedule_status)
              .filter((scheduleStatus: string) => scheduleStatus && scheduleStatus.trim() !== "")
          )
        ) as string[];

        // 🚀 Update unique values using generic approach
        setUniqueValues({
          status: statuses.sort(),
          user: users.sort(),
          schedule_status: scheduleStatuses.sort()
        });

        // 🚀 Update global variables for AG Grid custom filters
        globalStatuses = statuses.sort();
        globalUsers = users.sort();
        globalScheduleStatuses = scheduleStatuses.sort();

        setScanRowData(mappedData);

      }
      if (response.data?.data?.some((item: any) => item.status === 3)) {
        console.log("welcome");
        await assetCount()
      }
    } catch (error) {
      setIsLoading(false)
    } finally {
      setIsLoading(false)
    }
  };


  useEffect(() => {
    const scanDetails = scanRowData.map((data: any) => ({
      scan_uuid: data.scan_uuid,
      onprem_posture_id: data.onprem_posture_id,
      name: data.scanName,
    }));

    dispatch(setScanDetails(scanDetails));
  }, [scanRowData, dispatch]);

  const fetchScheduleData = async () => {
    try {
      setIsLoading(true);
      let url = "";
      if (module == "onpremises") {
        url = END_POINTS.ON_PREM_SCHEDULED_SCAN;
        //navigate(`/onprem/scan/${rowData.scan_uuid}`);
      } else {
        url = "securityscan/cloud-schedules";
        //navigate(`/cloud/scan/${rowData.scan_uuid}`);
      }

      url = url + `?timezone=Asia/Calcutta`;
      const response = await axiosInstance.get(url);
      if (response.data?.data?.summary_data) {
        dispatch(setScheduleList(response.data.data.summary_data));
        const mappedData = response.data.data.summary_data.map(
          (schedule: any, index: any) => ({
            id: index + 1,
            scanName: schedule.schedule_name,
            scanId: schedule.schedule_scan_id,
            frequency: schedule.frequecy_name,
            createdBy: schedule.user_name,
            date: schedule.date,
            scheduledTime: schedule.time,
            scheduledStatus: schedule.schedule_status,
            noOfAssets: schedule.num_of_assets,
          })
        );

        // 🚀 Extract unique values for schedule custom filters
        const frequencies = Array.from(
          new Set(
            mappedData
              .map((schedule: any) => schedule.frequency)
              .filter((frequency: string) => frequency && frequency.trim() !== "")
          )
        ) as string[];

        const createdByList = Array.from(
          new Set(
            mappedData
              .map((schedule: any) => schedule.createdBy)
              .filter((creator: string) => creator && creator.trim() !== "")
          )
        ) as string[];

        const scheduledStatuses = Array.from(
          new Set(
            mappedData
              .map((schedule: any) => schedule.scheduledStatus)
              .filter((status: string) => status && status.trim() !== "")
          )
        ) as string[];

        // 🚀 Update unique values for schedule filters
        setScheduleUniqueValues({
          frequency: frequencies.sort(),
          createdBy: createdByList.sort(),
          scheduledStatus: scheduledStatuses.sort()
        });

        // 🚀 Update global variables for schedule filters
        globalScheduleFrequencies = frequencies.sort();
        globalScheduleCreatedBy = createdByList.sort();
        globalScheduleScheduledStatuses = scheduledStatuses.sort();

        setScheduleRowData(mappedData);
        //setIsLoading(false)
      } else if (response.data?.data?.account_data) {
        console.log(
          "response.data?.data?.account_data",
          response.data?.data?.account_data
        );
        const mappedData = response.data.data.account_data.map(
          (schedule: any, index: any) => ({
            id: index + 1,
            scanName: schedule.account_id,
            scanId: schedule.scan_id,
            frequency: schedule.frequecy_name,
            createdBy: schedule.user_name,
            date: schedule.schedule_created_date,
            scheduledTime: schedule.time,
            scheduledStatus: schedule.schedule_status,
          })
        );

        // 🚀 Extract unique values for schedule custom filters (cloud)
        const frequencies = Array.from(
          new Set(
            mappedData
              .map((schedule: any) => schedule.frequency)
              .filter((frequency: string) => frequency && frequency.trim() !== "")
          )
        ) as string[];

        const createdByList = Array.from(
          new Set(
            mappedData
              .map((schedule: any) => schedule.createdBy)
              .filter((creator: string) => creator && creator.trim() !== "")
          )
        ) as string[];

        const scheduledStatuses = Array.from(
          new Set(
            mappedData
              .map((schedule: any) => schedule.scheduledStatus)
              .filter((status: string) => status && status.trim() !== "")
          )
        ) as string[];

        const scanNames = Array.from(
          new Set(
            mappedData
              .map((schedule: any) => schedule.scanName)
              .filter((scanName: string) => scanName && scanName.trim() !== "")
          )
        ) as string[];

        // 🚀 Update unique values for schedule filters
        setScheduleUniqueValues({
          frequency: frequencies.sort(),
          createdBy: createdByList.sort(),
          scheduledStatus: scheduledStatuses.sort(),
          scanName: scanNames.sort()
        });

        // 🚀 Update global variables for schedule filters
        globalScheduleFrequencies = frequencies.sort();
        globalScheduleCreatedBy = createdByList.sort();
        globalScheduleScheduledStatuses = scheduledStatuses.sort();
        globalScheduleScanNames = scanNames.sort();

        setScheduleRowData(mappedData);
        //setIsLoading(false)
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchScanData();
    fetchScheduleData();
    assetCount();
  }, []);

  const handleScanRowClick = (rowData: any) => {

    if ((rowData.status === "Success" || rowData.status === "Partially Success") && (Math.round(rowData.securityScore) >= 0 && Math.round(rowData?.misconfiguration) >= 0)) {
      dispatch(setEdit({ edit: true, scan_uuid: rowData.scan_uuid, status: rowData.statusTemp, scanTime: rowData.scanTime }));
      if (module == 'onpremises') {
        navigate(`/onprem/scan/${rowData.scan_uuid}`);
      } else {
        navigate(`/cloud/scan/${rowData.scan_uuid}`);
      }
      //navigate(`/onprem/scan/${rowData.scan_uuid}`);
    } else if (rowData.status === "Failed" && module == 'onpremises') {
      dispatch(setEdit({ edit: true, scan_uuid: rowData.scan_uuid, status: rowData.statusTemp, scanTime: rowData.scanTime }));
      // dispatch(myScanActions.scanInformaton({ scan_id: rowData.scan_uuid,statusTemp: rowData.statusTemp }))
      hasPermissions(ROLES_CONTENT_PROPS.SCAN_DETAILS) ? navigate(`/on-premises/scan-details/${rowData.scan_uuid}`) : toast.error("You do not have permission to view scan details");

    } else if ((rowData.statusTemp == 1 || rowData.statusTemp == 2) && module == 'onpremises') {
      dispatch(setEdit({ edit: true, scan_uuid: rowData.scan_uuid, status: rowData.statusTemp, scanTime: rowData.scanTime }));
      //dispatch(myScanActions.scanInformaton({ scan_id: rowData.scan_uuid,statusTemp: rowData.statusTemp }))
      hasPermissions(ROLES_CONTENT_PROPS.SCAN_DETAILS) ? navigate(`/on-premises/scan-details/${rowData.scan_uuid}`) : toast.error("You do not have permission to view scan details");
    }
    if (module == 'onpremises' && rowData.scheduledStatus) {
      navigate(`/onprem/schedule/${rowData.scanId}`);

    }
  };
  const handleScheduleRowClick = (rowData: any) => {
    if (module == 'onpremises' && rowData.scheduledStatus) {
      navigate(`/onprem/schedule/${rowData.scanId}`);

    }
    //navigate(`/onprem/schedule/${rowData.scanId}`);

  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setIsChecked(checked);
    if (checked) {
      setSearchParams({ view: "schedule" });
    } else {
      setSearchParams({});
    }
  };

  const handleAddSchedule = () => {
    navigate("/onprem/schedule");
  };

  const headerActions = (
    <Box display="flex" alignItems="center" gap={2}>
      <Typography sx={{ marginRight: "3px" }}>Scans</Typography>
      <FormControlLabel
        control={
          <IOSSwitch
            checked={isChecked}
            onChange={handleSwitchChange}
            width={40}
            height={20}
            thumbSize={16}
          />
        }
        label={
          <Typography variant="body2" sx={{ ml: 1, fontSize: "16px" }}>
            Schedule
          </Typography>
        }
        labelPlacement="end"
      />
      {isChecked &&
        module === "onpremises" &&
        hasPermissions(ROLES_CONTENT_PROPS.SCHEDULE_CREATE_MODIFY) && (
          <IconButton
            aria-label="delete"
            color="primary"
            onClick={handleAddSchedule}
          >
            <AddIcon />
          </IconButton>
        )}
    </Box>
  );

  const getSchduleStatusColor = (status: string) => {
    switch (status) {
      case "Active":
        return { color: "#00a61b", background: "rgba(0, 166, 27, .1)" };
      case "Pending":
        return { color: "#f53b3b", background: "rgba(245, 59, 59, .1)" };
      case "Completed":
        return { color: "#696595", background: "rgba(105, 101, 149, .1)" };
      default:
        return { color: "default" as "default" };
    }
  };


  // Define Scan Table columns with useMemo for better performance
  const scanColumnDefs: ColDef[] = useMemo(() => [
    // { field: "id", headerName: "#", filter: true, minWidth: 100 },
    {
      headerName: "#",
      field: 'id',
      minWidth: 80,
      valueGetter: (params) =>
        params.node && params.node.rowIndex !== null
          ? params.node.rowIndex + 1
          : null,
      sortable: false,
      filter: false,
    },
    {
      field: "scanName",
      headerName: "Scan Name",
      filter: false,
      sortable: true,
      minWidth: 200,
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.value) return null;

        return (
          <Tooltip
            title={params.value}
            arrow
            placement="top"
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: "preventOverflow",
                    options: {
                      boundary: "window",
                    },
                  },
                ],
              },
              tooltip: {
                sx: {
                  backgroundColor: "#fff",
                  color: "#000",
                  border: "1px solid #ddd",
                  boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                  fontSize: "14px",
                },
              },
              arrow: {
                sx: {
                  color: "#fff",
                },
              },
            }}
          >
            <span>{params.value}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "scanTime",
      headerName: "Scan Time",
      filter: ScheduleStatusFilter,
      filterParams: {
        scheduleStatuses: uniqueValues.schedule_status,
        isDarkMode: theme.palette.mode === 'dark',
      },
      minWidth: 200,
      //sort: "desc", // or "asc" for ascending

      cellRenderer: (params: ICellRendererParams) => {
        if (!params.value) return null;

        const formattedTime = moment(params.value).format(
          "DD-MMM-YYYY HH:mm:ss"
        );

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {(params.data.schedule !== null && params.data.schedule_status == 'scheduled') && (
              <Tooltip
                title={
                  <div
                    style={{
                      whiteSpace: "pre-line",
                      fontSize: "12px",
                      padding: 5,
                    }}
                  >
                    <strong>Schedule Information</strong>
                    <Divider sx={{ color: "#fff" }} />
                    Frequency: {params.data.schedule.frequency_name || "N/A"}
                    <br />
                    On date: {params.data.schedule.values.date || "Everyday"}
                    <br />
                    At time: {params.data.schedule.values.time || "12:00 AM"}
                  </div>
                }
                arrow
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: "preventOverflow",
                        options: {
                          boundary: "window",
                        },
                      },
                    ],
                  },
                  tooltip: {
                    sx: {
                      backgroundColor: "#fff",
                      color: "#000",
                      border: "1px solid #ddd",
                      boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                      fontSize: "14px",
                    },
                  },
                  arrow: {
                    sx: {
                      color: "#fff",
                    },
                  },
                }}
              >
                <img
                  src={calenderIcon}
                  alt="calendar"
                  style={{ width: "15px", height: "15px" }}
                />
              </Tooltip>
            )}

            {params.data.schedule_status && params.data.schedule_status == 'schedulable' && (
              <>
                <img
                  src={buleCalenderIcon}
                  alt="calendar"
                  style={{ width: "15px", height: "15px" }}
                />

              </>
            )}

            <span>{formattedTime}</span>
          </div>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      filter: StatusFilter,
      sortable: false,
      filterParams: {
        statuses: uniqueValues.status,
        isDarkMode: theme.palette.mode === 'dark',
      },
      minWidth: 200,
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.value) return null;
        let color = "";
        let background = "";
        let border = "";
        let dotColor = "";
        switch (params.value) {
          case "Success":
            color = "#00a61b";
            background = "rgba(0, 166, 27, 0.04)";
            border = "1.5px solid #00a61b";
            dotColor = "#00a61b";
            break;
          case "Failed":
            color = "#f53b3b";
            background = "rgba(245, 59, 59, 0.04)";
            border = "1.5px solid #f53b3b";
            dotColor = "#f53b3b";
            break;
          case "Cancelled":
            color = "#696595";
            background = "rgba(105, 101, 149, 0.04)";
            border = "1.5px solid #696595";
            dotColor = "#696595";
            break;
          case "Partially Success":
            color = "#F7941F";
            background = "rgba(247, 148, 31, 0.04)";
            border = "1.5px solid #F7941F";
            dotColor = "#F7941F";
            break;
          default:
            color = "#9E9E9E";
            background = "rgba(158, 158, 158, 0.04)";
            border = "1.5px solid #9E9E9E";
            dotColor = "#9E9E9E";
        }
        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "0 10px",
              borderRadius: "5px",
              fontWeight: 700,
              fontSize: "13px",
              color: color,
              background: background,
              border: border,
              minWidth: "80px",
              textAlign: "center",
              height: "24px",
              lineHeight: "20px",
              boxSizing: "border-box",
            }}
          >
            <span
              style={{
                width: 9,
                height: 9,
                borderRadius: "50%",
                backgroundColor: dotColor,
                display: "inline-block",
              }}
            />
            {params.value}
          </span>
        );
      },
    },

    {
      field: "assets", headerName: "Assets", filter: true, sortable: true, minWidth: 150, maxWidth: 130,
      cellRenderer: (params: ICellRendererParams) => {
        console.log('Assets value:', params.value);
        return params.value;
      }
    },
    {
      field: "securityScore",
      headerName: "Security Score",
      filter: true,
      sortable: true,
      minWidth: 150,
      cellRenderer: (params: ICellRendererParams) => {
        if (params.value === undefined || params.value === null) return null;
        const score = Math.round(params.value);
        let icon = green_shield;
        if (score <= 50) icon = red_shield;
        else if (score < 80) icon = orange_shield;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img src={icon} alt="Security Score" style={{ width: 20, height: 20 }} />
            <span title={params.value}>{score}</span>
          </div>
        );
      },
    },

    {
      field: "misconfiguration",
      headerName: "Misconfiguration",
      filter: true,
      sortable: true,
      minWidth: 180,
      cellRenderer: (params: any) => {
        if (params.value === undefined || params.value === null) return null;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <img
              src={misconfig}
              alt="Misconfiguration"
              style={{ width: "20px", height: "20px" }}
            />
            <span>{params.value}</span>
          </div>
        );
      }
    },

    {
      field: "user",
      headerName: "User",
      filter: UserFilter,
      filterParams: {
        users: uniqueValues.user,
        isDarkMode: theme.palette.mode === 'dark',
      },
      sortable: true,
      minWidth: 250,
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <Tooltip
            title={params.value}
            arrow
            placement="top"
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: "preventOverflow",
                    options: {
                      boundary: "window",
                    },
                  },
                ],
              },
              tooltip: {
                sx: {
                  backgroundColor: "#fff",
                  color: "#000",
                  border: "1px solid #ddd",
                  boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                  fontSize: "14px",
                },
              },
              arrow: {
                sx: {
                  color: "#fff",
                },
              },
            }}
          >
            <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>
              {params.value}
            </span>
          </Tooltip>
        );
      }
    },
    {
      field: "actions",
      headerName: "",
      minWidth: 100,
      maxWidth: 100,
      filter: false,
      sortable: false,
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.data) return null;
        return <div className="ag-cell-no-click" ><ActionMenu params={params} module={module} refetch={fetchScanData} /></div>
      },
      cellRendererParams: {
        suppressRowClickPropagation: true,
      },
    },
  ], [uniqueValues, theme.palette.mode, module]); // 🚀 Add dependencies for useMemo
  const agGridInlineStyles = useMemo(
    () => ({
      padding: "0px",
      // "& .ag-body-vertical-scroll": {
      //   width: "0px !important",
      //   minWidth: "0px !important",
      // },
      // "& .ag-body-vertical-scroll-viewport": {
      //   width: "0px !important",
      //   minWidth: "0px !important",
      // },
      "& .ag-center-cols-clipper": {
        marginRight: 0,
      },
      "& .ag-center-cols-viewport": {
        marginRight: 0,
      },
    }),
    []
  );

  const scheduleColumnDefs: ColDef[] = [
    { field: "id", headerName: "#", filter: false, minWidth: 60, sortable: false },
    {
      field: "scanName",
      headerName: module == "onpremises" ? "Scan Name" : "Account ID",
      filter: true,
      sortable: true,
      minWidth: 120,
    },
    {
      field: "scanId",
      headerName: "Scan ID",
      filter: false,
      sortable: false,
      minWidth: 250,
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <Tooltip
            title={params.value}
            arrow
            placement="bottom"
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: "preventOverflow",
                    options: {
                      boundary: "window",
                    },
                  },
                ],
              },
              tooltip: {
                sx: {
                  backgroundColor: "#fff",
                  color: "#000",
                  border: "1px solid #ddd",
                  boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                  fontSize: "14px",
                },
              },
              arrow: {
                sx: {
                  color: "#fff",
                },
              },
            }}
          >
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "block",
              }}
            >
              {params.value}
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "frequency",
      headerName: "Frequency",
      filter: FrequencyFilter,
      filterParams: {
        frequencies: scheduleUniqueValues.frequency,
        isDarkMode: theme.palette.mode === 'dark',
      },
      sortable: false,
      minWidth: 150,
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <Tooltip
            title={params.value}
            arrow
            placement="top"
            slotProps={{
              popper: {
                modifiers: [
                  {
                    name: "preventOverflow",
                    options: {
                      boundary: "window",
                    },
                  },
                ],
              },
              tooltip: {
                sx: {
                  backgroundColor: "#fff",
                  color: "#000",
                  border: "1px solid #ddd",
                  boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                  fontSize: "14px",
                },
              },
              arrow: {
                sx: {
                  color: "#fff",
                },
              },
            }}
          >
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "block",
              }}
            >
              {params.value}
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "createdBy",
      headerName: "Created By",
      filter: CreatedByFilter,
      filterParams: {
        createdBy: scheduleUniqueValues.createdBy,
        isDarkMode: theme.palette.mode === 'dark',
      },
      sortable: true,
      minWidth: 150,
    },
    {
      field: "scheduledTime",
      headerName: "Scheduled Time",
      sortable: true,
      filter: false,
      minWidth: 150,
    },
    {
      field: "scheduledStatus",
      headerName: "Scheduled Status",
      filter: ScheduledStatusFilter,
      filterParams: {
        scheduledStatuses: scheduleUniqueValues.scheduledStatus,
        isDarkMode: theme.palette.mode === 'dark',
      },
      sortable: false,
      minWidth: 150,
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.value) return null;
        const { color, background } = getSchduleStatusColor(params.value);

        return (
          <Chip
            label={
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {params.value}
              </span>
            }
            size="small"
            variant="outlined"
            sx={{
              fontWeight: "bold",
              color: color,
              maxWidth: "fit-content",
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              mt: 1,
              backgroundColor: background,
            }}
          />
        );
      },
    },
    ...(module === "onpremises"
      ? [
        {
          field: "noOfAssets",
          headerName: "Number of Assets",
          filter: true,
          minWidth: 150,
        },
      ]
      : []),
    { field: "date", headerName: "Date", sortable: true, filter: false, minWidth: 150 },
  ];

  const handleModalClose = () => {
    dispatch(
      myScanActions.renamePanel({
        isRenamePanelOpen: false,
        scan_id: "",
        scanName: "",
      })
    );
    dispatch(
      myScanActions.changeSchedule({
        isSchedulePanelOpen: false,
        frequency: "",
        scanName: "",
        atTime: "",
      })
    );
  };

  return (
    <div key={location.pathname}>
      <Card
        variant="outlined"
        sx={{ height: "calc(100vh - 120px)", borderRadius: 3 }}
      >
        {isChecked ? (
          <AgGridTable
            columnDefs={scheduleColumnDefs}
            styles={agGridInlineStyles}
            data={scheduleRowData}
            height={"calc(100vh - 160px)"}
            showSearch
            headerActions={
              hasPermissions(ROLES_CONTENT_PROPS.SCHEDULE) ? headerActions : ""
            }
            activeFilters={scheduleActiveFilters}
            onRowClick={handleScheduleRowClick}
            isLoading={isLoading}
          />
        ) : (
          <AgGridTable
            columnDefs={scanColumnDefs}
            styles={agGridInlineStyles}
            data={scanRowData}
            height={"calc(100vh - 160px)"}
            showSearch
            headerActions={
              hasPermissions(ROLES_CONTENT_PROPS.SCHEDULE) ? headerActions : ""
            }
            activeFilters={activeFilters}
            onRowClick={handleScanRowClick}
            isLoading={isLoading}
          />
        )}

        {renameDetails.isRenamePanelOpen && (
          <RenameModel
            open={renameDetails.isRenamePanelOpen}
            onClose={handleModalClose}
            scanId={renameDetails.scan_id}
            scanName={renameDetails.scanName}
            refetch={fetchScanData}
          />
        )}
        {renameDetails.isSchedulePanelOpen && (
          <ChangeScheduleModal
            open={renameDetails.isSchedulePanelOpen}
            onClose={handleModalClose}
            scheduleId={renameDetails.schedule_id}
            scheduleName={renameDetails.scanName}
            refetch={fetchScanData}
            module={module}
            type={renameDetails.type}
            initialValues={{
              scan_name: renameDetails.scanName,
              frequency_id: renameDetails.frequency_id,
              time: renameDetails.atTime,
              date: renameDetails.date,
              day: renameDetails.day,
              schedule_id: renameDetails.schedule_id,
              scan_id: renameDetails.scan_id,
              ip_range: renameDetails.ip_range,
            }}
            fetchScanData={fetchScanData}
          />
        )}
      </Card>
    </div>
  );
};

export default MyScanTable;
